import { Id } from "~/convex/_generated/dataModel";

// Demo IDs - using mock string IDs that won't conflict with real data
export const DEMO_IDS = {
  appId: "demo_app_123" as Id<"apps">,
  campaignId: "demo_campaign_123" as Id<"campaigns">,
  contractId: "demo_contract_123" as Id<"creatorContracts">,
  creatorId: "demo_creator_123" as Id<"creators">,
  userId: "demo_user_123" as Id<"users">,
};

// Demo App
export const DEMO_APP = {
  _id: DEMO_IDS.appId,
  name: "TechFlow Pro",
  description:
    "A revolutionary productivity app that helps users streamline their workflow with AI-powered suggestions and project management tools.",
  logo: "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=200&fit=crop&crop=center",
  _creationTime: Date.now() - 30 * 24 * 60 * 60 * 1000, // 30 days ago
};

// Demo Creators Data
const DEMO_CREATORS = [
  {
    id: "demo_creator_001",
    name: "Alex Thompson",
    avatarUrl:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
    followers: 125000,
    contractId: "demo_contract_001",
  },
  {
    id: "demo_creator_002",
    name: "Sarah Williams",
    avatarUrl:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop&crop=face",
    followers: 89000,
    contractId: "demo_contract_002",
  },
  {
    id: "demo_creator_003",
    name: "Michael Anderson",
    avatarUrl:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    followers: 156000,
    contractId: "demo_contract_003",
  },
  {
    id: "demo_creator_004",
    name: "Emma Wilson",
    avatarUrl:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
    followers: 203000,
    contractId: "demo_contract_004",
  },
  {
    id: "demo_creator_005",
    name: "James Mitchell",
    avatarUrl:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
    followers: 67000,
    contractId: "demo_contract_005",
  },
  {
    id: "demo_creator_006",
    name: "Lisa Brown",
    avatarUrl:
      "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face",
    followers: 98000,
    contractId: "demo_contract_006",
  },
  {
    id: "demo_creator_007",
    name: "Jake Davis",
    avatarUrl:
      "https://images.unsplash.com/photo-1507591064344-4c6ce005b128?w=150&h=150&fit=crop&crop=face",
    followers: 142000,
    contractId: "demo_contract_007",
  },
  {
    id: "demo_creator_008",
    name: "Sophie Taylor",
    avatarUrl:
      "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&h=150&fit=crop&crop=face",
    followers: 178000,
    contractId: "demo_contract_008",
  },
  {
    id: "demo_creator_009",
    name: "Marcus Johnson",
    avatarUrl:
      "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&h=150&fit=crop&crop=face",
    followers: 234000,
    contractId: "demo_contract_009",
  },
  {
    id: "demo_creator_010",
    name: "Rachel Chen",
    avatarUrl:
      "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face",
    followers: 87000,
    contractId: "demo_contract_010",
  },
  {
    id: "demo_creator_011",
    name: "David Rodriguez",
    avatarUrl:
      "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=150&h=150&fit=crop&crop=face",
    followers: 312000,
    contractId: "demo_contract_011",
  },
  {
    id: "demo_creator_012",
    name: "Olivia Parker",
    avatarUrl:
      "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=150&h=150&fit=crop&crop=face",
    followers: 145000,
    contractId: "demo_contract_012",
  },
  {
    id: "demo_creator_013",
    name: "Ryan Kim",
    avatarUrl:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    followers: 76000,
    contractId: "demo_contract_013",
  },
  {
    id: "demo_creator_014",
    name: "Maya Patel",
    avatarUrl:
      "https://images.unsplash.com/photo-1508186225823-0963cf9ab0de?w=150&h=150&fit=crop&crop=face",
    followers: 198000,
    contractId: "demo_contract_014",
  },
  {
    id: "demo_creator_015",
    name: "Tyler Brooks",
    avatarUrl:
      "https://images.unsplash.com/photo-1531427186611-ecfd6d936c79?w=150&h=150&fit=crop&crop=face",
    followers: 123000,
    contractId: "demo_contract_015",
  },
  {
    id: "demo_creator_016",
    name: "Zoe Martinez",
    avatarUrl:
      "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop&crop=face",
    followers: 267000,
    contractId: "demo_contract_016",
  },
];

// Function to generate demo campaign with custom app name
export const generateDemoCampaign = (appName: string = "TechFlow Pro") => ({
  _id: DEMO_IDS.campaignId,
  appId: DEMO_IDS.appId,
  name: `${appName} Launch Campaign`,
  type: "challenge" as const,
  status: "active" as const,
  isPublic: true,
  prizePool: 50000,
  applicationNotRequired: true,
  teamVideoReviewRequired: false,
  aiReviewRequired: false,
  creatorBrief: `We're launching ${appName}, a revolutionary productivity app that helps users streamline their workflow. We want creators to showcase how our app transforms daily tasks and improves efficiency.

Key features to highlight:
- AI-powered suggestions that understand context
- Integrated project management tools
- Real-time collaboration features
- Smart automation assistance

Show your audience how ${appName} saves time and reduces friction in real-world scenarios.`,
  validationRequirements: `Your video must include:
1. A genuine demonstration of using ${appName} for at least 2 minutes
2. Clear explanation of at least 3 key features
3. Before/after comparison of your workflow
4. Call-to-action directing viewers to download the app
5. Use of provided hashtags: #${appName.replace(/\s+/g, "")} #ProductivityHack #WorkflowTools`,
  manualVideoReviewRequired: true,
  endDate: Date.now() + 60 * 24 * 60 * 60 * 1000, // 60 days from now
  _creationTime: Date.now() - 25 * 24 * 60 * 60 * 1000, // 25 days ago
  // Add creatorContracts property for the campaigns list
  creatorContracts: DEMO_CREATORS.map((creator) => ({
    _id: creator.contractId as Id<"creatorContracts">,
    status: "active" as const,
    creatorId: creator.id as Id<"creators">,
    campaignId: DEMO_IDS.campaignId,
    appId: DEMO_IDS.appId,
  })),
  // Stats for CreatorGrid team
  enrolledCreatorsCount: DEMO_CREATORS.length, // All demo creators are enrolled
  submittedCreatorsCount: Math.floor(DEMO_CREATORS.length * 0.8), // 80% have submitted
  approvedCreatorsCount: Math.floor(DEMO_CREATORS.length * 0.6), // 60% have been approved
  predefinedCpm: 5.0, // $5 CPM for all creators
});

// Default campaign for backward compatibility
export const DEMO_CAMPAIGN = generateDemoCampaign();

// Demo Navigation Item
export const DEMO_NAV_ITEM = {
  name: "TechFlow Pro Launch (Demo)",
  logo: DEMO_APP.logo,
  appId: DEMO_IDS.appId,
};

// Demo Videos Data
const generateDemoVideo = (
  id: string,
  title: string,
  platform: string,
  views: number,
  likes: number,
  comments: number,
  shares: number,
  creatorId: string,
  status:
    | "approved"
    | "rejected"
    | "ai_reviewing"
    | "needs_manual_review" = "approved",
  earnings: number = 0,
  daysAgo: number = 1,
) => {
  // Use actual working video URLs for demo
  let url: string;
  if (platform === "youtube") {
    url = "https://www.youtube.com/shorts/oXwujuphEMc";
  } else if (platform === "tiktok") {
    url = "https://www.tiktok.com/@aliabdaal/video/7511358278228626710?lang=en";
  } else {
    // Instagram
    url = "https://www.instagram.com/reel/DL-Ffe9PFpV/";
  }

  return {
    _id: `demo_video_${id}` as Id<"contractVideos">,
    contractId:
      `demo_contract_${creatorId.split("_")[2]}` as Id<"creatorContracts">,
    appId: DEMO_IDS.appId,
    creatorId: creatorId as Id<"creators">,
    url,
    title,
    platform,
    views,
    likes,
    comments,
    shares,
    bookmarks: Math.floor(likes * 0.1),
    status,
    earnings,
    postDate: Date.now() - daysAgo * 24 * 60 * 60 * 1000,
    lastUpdated: Date.now() - daysAgo * 24 * 60 * 60 * 1000,
    _creationTime: Date.now() - daysAgo * 24 * 60 * 60 * 1000,
    // Additional properties to match Video type
    rejectionReason:
      status === "rejected"
        ? "Content doesn't showcase key features clearly"
        : undefined,
    submittedAt: Date.now() - daysAgo * 24 * 60 * 60 * 1000,
    approvedAt:
      status === "approved"
        ? Date.now() - (daysAgo - 1) * 24 * 60 * 60 * 1000
        : undefined,
    aiReviewScore:
      status === "approved" ? 0.85 + Math.random() * 0.15 : Math.random() * 0.7,
    manualReviewRequired: status === "needs_manual_review",
    thumbnailUrl: undefined,
    duration: Math.floor(Math.random() * 120) + 30, // 30-150 seconds
    description: `Demo video content for ${title}`,
    hashtags: ["#TechFlowPro", "#productivity", "#coding"],
    mentions: [],
    musicTrack: undefined,
    location: undefined,
    isSponsored: true,
    sponsorshipDisclosure: "Sponsored by TechFlow Pro",
  };
};

// Generic video titles that can work with any app
const GENERIC_VIDEO_TITLES = [
  // Alex Thompson's videos (demo_creator_001) - 4 videos
  {
    title: "🚀 This App Changed Everything! Full Review",
    platform: "youtube",
  },
  { title: "Productivity Hack You NEED to Try!", platform: "tiktok" },
  {
    title: "Before vs After: My Transformation",
    platform: "instagram",
  },
  {
    title: "Quick Setup Guide: Getting Started in 5 Minutes",
    platform: "youtube",
  },

  // Sarah Williams's videos (demo_creator_002) - 3 videos
  { title: "The Ultimate Companion App", platform: "youtube" },
  { title: "Why Everyone Should Use This App", platform: "tiktok" },
  { title: "My Honest Review After 30 Days", platform: "youtube" },

  // Michael Anderson's videos (demo_creator_003) - 8 videos
  { title: "This App Made Me 10x More Productive", platform: "tiktok" },
  {
    title: "New App vs Traditional Tools: Ultimate Comparison",
    platform: "youtube",
  },
  {
    title: "Day in the Life: Using This Game-Changer",
    platform: "instagram",
  },
  { title: "How This App Saved Me 20 Hours This Week", platform: "tiktok" },
  {
    title: "Advanced Features Deep Dive: What You're Missing",
    platform: "youtube",
  },
  { title: "Team Collaboration Made Simple", platform: "tiktok" },
  { title: "AI-Powered Features: A Game Changer", platform: "youtube" },
  { title: "From Beginner to Pro: My Journey", platform: "instagram" },

  // Emma Wilson's videos (demo_creator_004) - 6 videos
  {
    title: "Senior Professional Reacts to New App",
    platform: "youtube",
  },
  { title: "App Features That Will Blow Your Mind", platform: "tiktok" },
  { title: "From Burnout to Breakthrough: My Journey", platform: "instagram" },
  { title: "Building Something Amazing with New Tools", platform: "youtube" },
  { title: "Review Process Revolutionized", platform: "tiktok" },
  { title: "Performance Optimization Secrets", platform: "youtube" },

  // James Mitchell's videos (demo_creator_005) - 4 videos
  { title: "Game Changer for Beginners", platform: "youtube" },
  { title: "Quick Tips for Better Results", platform: "tiktok" },
  { title: "My First Month Experience", platform: "instagram" },
  { title: "Learning Curve: What I Wish I Knew", platform: "youtube" },

  // Lisa Brown's videos (demo_creator_006) - 7 videos
  {
    title: "New Tool vs Popular Alternative: Honest Comparison",
    platform: "youtube",
  },
  { title: "Why I Switched to This App", platform: "tiktok" },
  { title: "Workflow Setup Guide", platform: "instagram" },
  { title: "Advanced Features Deep Dive", platform: "youtube" },
  { title: "Productivity Hacks That Actually Work", platform: "tiktok" },
  { title: "Quality Improvement Tips", platform: "youtube" },
  { title: "Remote Work Efficiency Boost", platform: "instagram" },

  // Jake Davis's videos (demo_creator_007) - 3 videos
  { title: "The Future is Here", platform: "youtube" },
  { title: "Speed Run with New Tools", platform: "tiktok" },
  { title: "Tips from a Pro", platform: "instagram" },

  // Sophie Taylor's videos (demo_creator_008) - 5 videos
  { title: "How This App Transformed My Business", platform: "youtube" },
  { title: "Must-Have Tool for Remote Workers", platform: "tiktok" },
  { title: "Building Better Results with New Tools", platform: "instagram" },
  { title: "1 Month Results & ROI Analysis", platform: "youtube" },
  { title: "Scaling Team Efficiency", platform: "tiktok" },

  // Marcus Johnson's videos (demo_creator_009) - 6 videos
  {
    title: "Enterprise User Reviews New Productivity App",
    platform: "youtube",
  },
  { title: "Corporate Workflow Transformation", platform: "tiktok" },
  { title: "Leadership Perspective: Why We Switched", platform: "youtube" },
  { title: "Team Adoption Success Story", platform: "instagram" },
  { title: "ROI Analysis After 6 Months", platform: "youtube" },
  { title: "Scaling Across Multiple Departments", platform: "tiktok" },

  // Rachel Chen's videos (demo_creator_010) - 4 videos
  { title: "Student Life Made Easier", platform: "tiktok" },
  { title: "Study Organization Revolution", platform: "youtube" },
  { title: "Academic Productivity Boost", platform: "instagram" },
  { title: "Campus Life Integration Tips", platform: "tiktok" },

  // David Rodriguez's videos (demo_creator_011) - 7 videos
  { title: "Tech Expert's Deep Dive Review", platform: "youtube" },
  { title: "API Integration Possibilities", platform: "youtube" },
  { title: "Developer's Perspective on New Tools", platform: "tiktok" },
  { title: "Custom Automation Setup Guide", platform: "youtube" },
  { title: "Code Review Process Enhancement", platform: "instagram" },
  { title: "DevOps Team Efficiency Gains", platform: "tiktok" },
  { title: "Technical Architecture Breakdown", platform: "youtube" },

  // Olivia Parker's videos (demo_creator_012) - 5 videos
  { title: "Small Business Owner's Experience", platform: "youtube" },
  { title: "Budget-Friendly Productivity Solution", platform: "tiktok" },
  { title: "Client Management Made Simple", platform: "instagram" },
  { title: "Freelancer's Essential Tool Review", platform: "youtube" },
  { title: "Work-Life Balance Improvement", platform: "tiktok" },

  // Ryan Kim's videos (demo_creator_013) - 3 videos
  { title: "Gaming Content Creator Setup", platform: "tiktok" },
  { title: "Streaming Workflow Optimization", platform: "youtube" },
  { title: "Content Planning Revolution", platform: "instagram" },

  // Maya Patel's videos (demo_creator_014) - 6 videos
  { title: "Healthcare Professional's Review", platform: "youtube" },
  { title: "Medical Practice Efficiency", platform: "tiktok" },
  { title: "Patient Care Coordination Tools", platform: "youtube" },
  { title: "Clinical Workflow Improvements", platform: "instagram" },
  { title: "Telemedicine Integration Success", platform: "youtube" },
  { title: "Healthcare Team Collaboration", platform: "tiktok" },

  // Tyler Brooks's videos (demo_creator_015) - 4 videos
  { title: "Creative Agency Workflow Upgrade", platform: "youtube" },
  { title: "Design Process Revolution", platform: "tiktok" },
  { title: "Client Project Management", platform: "instagram" },
  { title: "Creative Team Collaboration", platform: "youtube" },

  // Zoe Martinez's videos (demo_creator_016) - 5 videos
  { title: "Marketing Professional's Take", platform: "youtube" },
  { title: "Campaign Management Simplified", platform: "tiktok" },
  { title: "Social Media Strategy Enhancement", platform: "instagram" },
  { title: "Analytics Integration Success", platform: "youtube" },
  { title: "Multi-Platform Campaign Coordination", platform: "tiktok" },

  // Pending/review videos
  { title: "Security Features Review", platform: "youtube" },
  { title: "Is This App Worth the Hype?", platform: "tiktok" },
  { title: "Enterprise Features Overview", platform: "youtube" },
  { title: "Integration Tutorial", platform: "instagram" },

  // Rejected videos
  { title: "Why This App Might Not Be For Everyone", platform: "youtube" },
  { title: "Pricing Concerns Discussion", platform: "tiktok" },

  // Additional video titles to match expanded stats (22 more needed)
  // Marcus Johnson additional videos
  { title: "Executive Leadership Review", platform: "youtube" },
  { title: "Corporate Implementation Guide", platform: "tiktok" },
  { title: "Business Process Optimization", platform: "instagram" },

  // Rachel Chen additional videos
  { title: "Study Buddy App Review", platform: "tiktok" },
  { title: "College Student's Productivity Tips", platform: "youtube" },

  // David Rodriguez additional videos
  { title: "Advanced Integration Tutorial", platform: "youtube" },
  { title: "Security Architecture Review", platform: "youtube" },
  { title: "Performance Benchmarking", platform: "tiktok" },

  // Olivia Parker additional videos
  { title: "Entrepreneur's Essential Tools", platform: "youtube" },
  { title: "Startup Productivity Stack", platform: "tiktok" },

  // Ryan Kim additional videos
  { title: "Creator Economy Tools", platform: "tiktok" },

  // Maya Patel additional videos
  { title: "Medical Professional's Workflow", platform: "youtube" },
  { title: "Healthcare Efficiency Solutions", platform: "tiktok" },

  // Tyler Brooks additional videos
  { title: "Design Agency Transformation", platform: "youtube" },

  // Zoe Martinez additional videos
  { title: "Digital Marketing Integration", platform: "youtube" },

  // Mixed creator additional videos
  { title: "Quick Start Guide", platform: "youtube" },
  { title: "Power User Tips", platform: "tiktok" },
  { title: "Advanced Features Walkthrough", platform: "instagram" },
  { title: "Mobile App Review", platform: "tiktok" },
  { title: "Cross-Platform Sync Test", platform: "youtube" },
  { title: "Team Onboarding Process", platform: "instagram" },
  { title: "Productivity Metrics Analysis", platform: "youtube" },
];

// Video stats data (views, likes, comments, shares, creator, status, earnings, daysAgo)
const VIDEO_STATS = [
  // Alex Thompson - 4 videos (moderate creator) - reduced views by ~25%
  [34000, 2400, 135, 340, "demo_creator_001", "approved", 170, 2],
  [29000, 2100, 115, 290, "demo_creator_001", "approved", 145, 3],
  [39000, 3100, 165, 390, "demo_creator_001", "approved", 195, 4],
  [26000, 1900, 90, 260, "demo_creator_001", "approved", 130, 5],

  // Sarah Williams - 3 videos (consistent creator) - reduced views by ~25%
  [47000, 3600, 210, 470, "demo_creator_002", "approved", 235, 1],
  [44000, 3300, 190, 440, "demo_creator_002", "approved", 220, 2],
  [41000, 3100, 175, 410, "demo_creator_002", "approved", 205, 3],

  // Michael Anderson - 8 videos (very active creator) - reduced views by ~30%
  [55000, 4300, 245, 550, "demo_creator_003", "approved", 275, 8],
  [50000, 3900, 224, 500, "demo_creator_003", "approved", 250, 1],
  [46000, 3500, 196, 460, "demo_creator_003", "approved", 230, 2],
  [62000, 4900, 280, 620, "demo_creator_003", "approved", 310, 3],
  [57000, 4500, 252, 570, "demo_creator_003", "approved", 285, 4],
  [53000, 4100, 231, 530, "demo_creator_003", "approved", 265, 5],
  [48000, 3700, 203, 480, "demo_creator_003", "approved", 240, 6],
  [52000, 4100, 217, 520, "demo_creator_003", "approved", 260, 7],

  // Emma Wilson - 6 videos (active creator) - reduced views by ~25%
  [53000, 4100, 233, 530, "demo_creator_004", "approved", 265, 4],
  [47000, 3600, 195, 470, "demo_creator_004", "approved", 235, 5],
  [64000, 5000, 285, 640, "demo_creator_004", "approved", 320, 2],
  [44000, 3400, 180, 440, "demo_creator_004", "approved", 220, 3],
  [58000, 4500, 255, 580, "demo_creator_004", "approved", 290, 6],
  [52000, 4100, 225, 520, "demo_creator_004", "approved", 260, 7],

  // James Mitchell - 4 videos (moderate creator) - reduced views by ~25%
  [32000, 2400, 128, 320, "demo_creator_005", "approved", 160, 6],
  [42000, 3200, 180, 420, "demo_creator_005", "approved", 210, 7],
  [36000, 2800, 150, 360, "demo_creator_005", "approved", 180, 8],
  [39000, 3000, 165, 390, "demo_creator_005", "approved", 195, 9],

  // Lisa Brown - 7 videos (very active creator) - reduced views by ~25%
  [50000, 3800, 218, 500, "demo_creator_006", "approved", 250, 3],
  [55000, 4300, 248, 550, "demo_creator_006", "approved", 275, 4],
  [44000, 3300, 188, 440, "demo_creator_006", "approved", 220, 5],
  [60000, 4700, 263, 600, "demo_creator_006", "approved", 300, 1],
  [48000, 3700, 203, 480, "demo_creator_006", "approved", 240, 6],
  [54000, 4200, 240, 540, "demo_creator_006", "approved", 270, 7],
  [51000, 3900, 218, 510, "demo_creator_006", "approved", 255, 8],

  // Jake Davis - 3 videos (new creator) - reduced views by ~30%
  [67000, 5300, 294, 670, "demo_creator_007", "approved", 335, 2],
  [62000, 4800, 273, 620, "demo_creator_007", "approved", 310, 3],
  [64000, 5000, 287, 640, "demo_creator_007", "approved", 320, 4],

  // Sophie Taylor - 5 videos (active creator) - reduced views by ~25%
  [53000, 4100, 225, 530, "demo_creator_008", "approved", 265, 3],
  [56000, 4400, 248, 560, "demo_creator_008", "approved", 280, 4],
  [41000, 3100, 173, 410, "demo_creator_008", "approved", 205, 5],
  [51000, 3900, 218, 510, "demo_creator_008", "approved", 255, 6],
  [62000, 4800, 270, 620, "demo_creator_008", "approved", 310, 7],

  // Additional approved videos - reduced views by ~25%
  [34000, 2600, 135, 340, "demo_creator_002", "approved", 170, 1],
  [39000, 3000, 165, 390, "demo_creator_005", "approved", 195, 2],
  [29000, 2200, 113, 290, "demo_creator_007", "approved", 145, 1],
  [31000, 2300, 128, 310, "demo_creator_006", "approved", 155, 3],

  // More approved videos - reduced views by ~25%
  [21000, 1600, 90, 210, "demo_creator_003", "approved", 105, 4],
  [24000, 1800, 105, 240, "demo_creator_008", "approved", 120, 5],

  // Marcus Johnson - 6 videos (high-performing enterprise creator) - reduced views by ~35%
  [81000, 6400, 358, 810, "demo_creator_009", "approved", 405, 1],
  [77000, 6000, 338, 770, "demo_creator_009", "approved", 385, 2],
  [86000, 6800, 377, 860, "demo_creator_009", "approved", 430, 3],
  [62000, 4800, 267, 620, "demo_creator_009", "approved", 310, 4],
  [70000, 5600, 312, 700, "demo_creator_009", "approved", 350, 5],
  [92000, 7300, 410, 920, "demo_creator_009", "approved", 460, 6],

  // Rachel Chen - 4 videos (student/young audience creator) - reduced views by ~25%
  [26000, 2100, 120, 260, "demo_creator_010", "approved", 130, 2],
  [32000, 2600, 143, 320, "demo_creator_010", "approved", 160, 3],
  [29000, 2300, 128, 290, "demo_creator_010", "approved", 145, 4],
  [23000, 1900, 105, 230, "demo_creator_010", "approved", 115, 5],

  // David Rodriguez - 7 videos (tech expert with high engagement) - reduced views by ~40%
  [94000, 7400, 414, 940, "demo_creator_011", "approved", 470, 1],
  [86000, 6900, 384, 860, "demo_creator_011", "approved", 430, 2],
  [100000, 8000, 444, 1000, "demo_creator_011", "approved", 500, 3],
  [80000, 6400, 354, 800, "demo_creator_011", "approved", 400, 4],
  [89000, 7100, 396, 890, "demo_creator_011", "approved", 445, 5],
  [77000, 6100, 342, 770, "demo_creator_011", "approved", 385, 6],
  [103000, 8200, 456, 1030, "demo_creator_011", "approved", 515, 7],

  // Olivia Parker - 5 videos (small business focused) - reduced views by ~30%
  [47000, 3700, 203, 470, "demo_creator_012", "approved", 235, 2],
  [41000, 3200, 175, 410, "demo_creator_012", "approved", 205, 3],
  [52000, 4100, 231, 520, "demo_creator_012", "approved", 260, 4],
  [43000, 3400, 189, 430, "demo_creator_012", "approved", 215, 5],
  [55000, 4400, 245, 550, "demo_creator_012", "approved", 275, 6],

  // Ryan Kim - 3 videos (gaming/streaming creator) - reduced views by ~30%
  [34000, 2700, 147, 340, "demo_creator_013", "approved", 170, 3],
  [39000, 3100, 168, 390, "demo_creator_013", "approved", 195, 4],
  [30000, 2400, 133, 300, "demo_creator_013", "approved", 150, 5],

  // Maya Patel - 6 videos (healthcare professional) - reduced views by ~30%
  [62000, 5000, 273, 620, "demo_creator_014", "approved", 310, 1],
  [53000, 4300, 238, 530, "demo_creator_014", "approved", 265, 2],
  [64000, 5200, 287, 640, "demo_creator_014", "approved", 320, 3],
  [58000, 4600, 259, 580, "demo_creator_014", "approved", 290, 4],
  [68000, 5500, 301, 680, "demo_creator_014", "approved", 340, 5],
  [50000, 4000, 224, 500, "demo_creator_014", "approved", 250, 6],

  // Tyler Brooks - 4 videos (creative agency) - reduced views by ~30%
  [45000, 3600, 196, 450, "demo_creator_015", "approved", 225, 2],
  [41000, 3300, 182, 410, "demo_creator_015", "approved", 205, 3],
  [50000, 4000, 224, 500, "demo_creator_015", "approved", 250, 4],
  [46000, 3700, 203, 460, "demo_creator_015", "approved", 230, 5],

  // Zoe Martinez - 5 videos (marketing professional) - reduced views by ~35%
  [67000, 5300, 299, 670, "demo_creator_016", "approved", 335, 1],
  [62000, 5000, 280, 620, "demo_creator_016", "approved", 310, 2],
  [57000, 4500, 254, 570, "demo_creator_016", "approved", 285, 3],
  [73000, 5800, 325, 730, "demo_creator_016", "approved", 365, 4],
  [59000, 4700, 267, 590, "demo_creator_016", "approved", 295, 5],

  // Additional mixed creator videos for more variety - reduced views by ~25%
  [19000, 1500, 83, 190, "demo_creator_010", "approved", 95, 1],
  [25000, 2000, 113, 250, "demo_creator_013", "approved", 125, 2],
  [35000, 2900, 158, 350, "demo_creator_015", "approved", 175, 1],
  [41000, 3200, 180, 410, "demo_creator_012", "approved", 205, 1],
  [29000, 2300, 128, 290, "demo_creator_014", "approved", 145, 7],

  // Previously pending/rejected videos now approved - reduced views by ~30%
  [43000, 3400, 189, 430, "demo_creator_009", "approved", 215, 1],
  [32000, 2500, 140, 320, "demo_creator_011", "approved", 160, 1],
  [27000, 2100, 119, 270, "demo_creator_016", "approved", 135, 2],

  // Previously rejected videos now approved - reduced views by ~25%
  [22000, 1700, 98, 220, "demo_creator_010", "approved", 110, 6],
  [31000, 2500, 135, 310, "demo_creator_013", "approved", 155, 7],

  // Additional videos posted 14-18 days ago for better campaign timeline distribution
  [45000, 3500, 195, 450, "demo_creator_001", "approved", 225, 14],
  [38000, 2900, 162, 380, "demo_creator_002", "approved", 190, 14],
  [52000, 4100, 230, 520, "demo_creator_003", "approved", 260, 15],
  [41000, 3200, 178, 410, "demo_creator_004", "approved", 205, 15],
  [36000, 2800, 154, 360, "demo_creator_005", "approved", 180, 16],
  [49000, 3800, 210, 490, "demo_creator_006", "approved", 245, 16],
  [43000, 3400, 189, 430, "demo_creator_007", "approved", 215, 17],
  [47000, 3700, 203, 470, "demo_creator_008", "approved", 235, 17],
  [55000, 4300, 242, 550, "demo_creator_009", "approved", 275, 18],
  [33000, 2600, 143, 330, "demo_creator_010", "approved", 165, 18],
  [58000, 4600, 253, 580, "demo_creator_011", "approved", 290, 14],
  [40000, 3100, 172, 400, "demo_creator_012", "approved", 200, 15],
  [35000, 2700, 149, 350, "demo_creator_013", "approved", 175, 16],
  [51000, 4000, 224, 510, "demo_creator_014", "approved", 255, 17],
  [44000, 3400, 187, 440, "demo_creator_015", "approved", 220, 18],
  [48000, 3800, 208, 480, "demo_creator_016", "approved", 240, 18],
];

// Function to generate demo videos with custom app name
export const generateDemoVideos = (_appName: string = "TechFlow Pro") => {
  return GENERIC_VIDEO_TITLES.map((videoData, index) => {
    const stats = VIDEO_STATS[index];
    if (!stats) return null;

    const [
      views,
      likes,
      comments,
      shares,
      creatorId,
      status,
      earnings,
      daysAgo,
    ] = stats;

    return generateDemoVideo(
      String(index + 1).padStart(3, "0"),
      videoData.title,
      videoData.platform,
      views as number,
      likes as number,
      comments as number,
      shares as number,
      creatorId as string,
      status as
        | "approved"
        | "rejected"
        | "ai_reviewing"
        | "needs_manual_review",
      earnings as number,
      daysAgo as number,
    );
  }).filter((v): v is NonNullable<typeof v> => v !== null);
};

// Default videos for backward compatibility
export const DEMO_VIDEOS = generateDemoVideos();

// Note: Creator-specific demo data removed - demo mode is only for app founders

// Demo App Founder Dashboard Data
export const getDemoAppFounderDashboard = (
  appName: string = "TechFlow Pro",
) => {
  const allVideos = generateDemoVideos(appName);
  const approvedVideos = allVideos.filter((v) => v.status === "approved");
  const totalCampaignEarnings = approvedVideos.reduce(
    (sum, v) => sum + v.earnings,
    0,
  );

  // Count videos by status
  const approvedCount = allVideos.filter((v) => v.status === "approved").length;
  const pendingCount = allVideos.filter(
    (v) => v.status === "ai_reviewing" || v.status === "needs_manual_review",
  ).length;
  const rejectedCount = allVideos.filter((v) => v.status === "rejected").length;

  return {
    publishedVideos: allVideos,
    videoStatusCountsByContractStatus: {
      active: {
        draft: 0,
        review: pendingCount,
        approved: approvedCount,
        needs_revision: rejectedCount,
      },
    } as Record<
      string,
      {
        draft: number;
        review: number;
        approved: number;
        needs_revision: number;
      }
    >,
    unpaidCreatorContractsToPay: totalCampaignEarnings,
    allCreators: DEMO_CREATORS.map((creator) => ({
      _id: creator.id as Id<"creators">,
      name: creator.name,
      contract: {
        _id: creator.contractId as Id<"creatorContracts">,
        status: "active" as const,
        creatorId: creator.id as Id<"creators">,
        campaignId: DEMO_IDS.campaignId,
        appId: DEMO_IDS.appId,
      },
    })),
    campaign: DEMO_CAMPAIGN,
    appCreatorContracts: DEMO_CREATORS.map((creator, _) => ({
      _id: creator.contractId as Id<"creatorContracts">,
      status: "active" as const,
      creatorId: creator.id as Id<"creators">,
      campaignId: DEMO_IDS.campaignId,
      appId: DEMO_IDS.appId,
      paidAmount: 0,
      startDate: Date.now() - 30 * 24 * 60 * 60 * 1000, // 30 days ago
      endDate: Date.now() + 60 * 24 * 60 * 60 * 1000, // 60 days from now
      flatFee: {
        perVideo: 0, // No flat fee per video
        timePeriod: {
          days: 30,
          amount: 0, // No monthly flat fee
        },
      },
      cpmRate: {
        type: "perVideo" as const,
        rate: 5.0, // $5 CPM for all creators
        threshold: 1000, // Minimum views for CPM
        maxPayout: undefined, // No max payout
      },
    })),
    totalCampaignEarnings,
    remainingBudget: DEMO_CAMPAIGN.prizePool - totalCampaignEarnings,
    videoProjectsDetails: getDemoVideoProjects(appName).map((project) => ({
      creatorName:
        DEMO_CREATORS.find((c) => c.id === project.creatorId)?.name ||
        "Demo Creator",
      projectTitle: project.title,
      status: project.status,
      contractStatus: "active" as const,
      projectId: project.projectId,
      creatorId: project.creatorId as Id<"creators">,
      contractId: project.contractId as Id<"creatorContracts">,
      createdAt: project.createdAt,
      linkedVideoTitle: null,
    })),
  };
};

// Helper function to generate realistic 30-day historical data
const generateHistoricalData = (
  days: number,
  totalViews: number,
  totalVideos: number,
) => {
  const historicalData = [];
  const today = new Date();

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dateString = date.toISOString().split("T")[0];

    // Create a realistic growth curve that starts slow and accelerates
    // Using an exponential-like curve but capped to prevent unrealistic spikes
    let progress;
    if (i === days - 1) {
      // Start from 0 on day 1
      progress = 0;
    } else {
      // Exponential growth curve that reaches 100% by the last day
      const dayProgress = (days - 1 - i) / (days - 1);
      // Use a power curve for more realistic growth (slow start, then acceleration)
      progress = Math.pow(dayProgress, 1.8);
    }

    // Add some realistic daily variance (±5-10%)
    const variance = 0.85 + Math.random() * 0.3; // Random between 0.85 and 1.15
    const adjustedProgress = Math.min(progress * variance, 1);

    historicalData.push({
      date: dateString,
      views: Math.floor(totalViews * adjustedProgress),
      likes: 0,
      comments: 0,
      shares: 0,
      videosWithData: Math.max(1, Math.floor(totalVideos * adjustedProgress)),
    });
  }

  return historicalData;
};

// Helper function to generate realistic creator signup data over 30 days
const generateCreatorSignupData = (totalCreators: number = 16) => {
  const signupData = [];
  const today = new Date();
  let cumulativeSignups = 0;

  // Create a realistic signup pattern over 30 days
  // Most signups happen in the first 2 weeks, then taper off
  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dateString = date.toISOString().split("T")[0];

    let dailySignups = 0;

    // Higher signup rate in first 2 weeks, especially first week
    // Adjusted for 16 creators to ensure better distribution
    if (i >= 23) {
      // Days 1-7: Heavy initial signups
      dailySignups =
        Math.random() < 0.8
          ? Math.random() < 0.6
            ? 1
            : Math.random() < 0.7
              ? 2
              : 3
          : 0;
    } else if (i >= 16) {
      // Days 8-14: Steady growth
      dailySignups = Math.random() < 0.6 ? (Math.random() < 0.8 ? 1 : 2) : 0;
    } else if (i >= 7) {
      // Days 15-23: Moderate signups
      dailySignups = Math.random() < 0.3 ? 1 : 0;
    } else {
      // Days 24-30: Final stragglers
      dailySignups = Math.random() < 0.15 ? 1 : 0;
    }

    cumulativeSignups += dailySignups;

    // Cap at total creators
    if (cumulativeSignups > totalCreators) {
      dailySignups -= cumulativeSignups - totalCreators;
      cumulativeSignups = totalCreators;
    }

    if (dailySignups > 0 || cumulativeSignups > 0) {
      signupData.push({
        date: dateString,
        signups: dailySignups,
        cumulativeSignups: cumulativeSignups,
      });
    }
  }

  return signupData;
};

// Demo Analytics Data for Campaign Analytics
export const getDemoCampaignAnalytics = (appName: string = "TechFlow Pro") => {
  const allVideos = generateDemoVideos(appName);
  const approvedVideos = allVideos.filter((v) => v.status === "approved");
  const pendingVideos = allVideos.filter(
    (v) => v.status === "ai_reviewing" || v.status === "needs_manual_review",
  );
  const rejectedVideos = allVideos.filter((v) => v.status === "rejected");

  // Calculate totals by platform
  const youtubeVideos = allVideos.filter((v) => v.platform === "youtube");
  const tiktokVideos = allVideos.filter((v) => v.platform === "tiktok");
  const instagramVideos = allVideos.filter((v) => v.platform === "instagram");

  const youtubeTotalViews = youtubeVideos.reduce((sum, v) => sum + v.views, 0);
  const tiktokTotalViews = tiktokVideos.reduce((sum, v) => sum + v.views, 0);
  const instagramTotalViews = instagramVideos.reduce(
    (sum, v) => sum + v.views,
    0,
  );

  const totalViews = youtubeTotalViews + tiktokTotalViews + instagramTotalViews;

  // Calculate actual median views from all videos
  const allViewCounts = allVideos.map((v) => v.views).sort((a, b) => a - b);
  const medianViews =
    allViewCounts.length % 2 === 0
      ? (allViewCounts[Math.floor(allViewCounts.length / 2) - 1] +
          allViewCounts[Math.floor(allViewCounts.length / 2)]) /
        2
      : allViewCounts[Math.floor(allViewCounts.length / 2)];

  return {
    platforms: [
      {
        platform: "youtube",
        totalVideos: youtubeVideos.length,
        currentViews: youtubeTotalViews,
        currentLikes: youtubeVideos.reduce((sum, v) => sum + v.likes, 0),
        currentComments: youtubeVideos.reduce((sum, v) => sum + v.comments, 0),
        currentShares: youtubeVideos.reduce((sum, v) => sum + v.shares, 0),
        viewsGrowth24h: 12.8,
        likesGrowth24h: 15.3,
        commentsGrowth24h: 18.7,
        sharesGrowth24h: 14.2,
        historicalData: generateHistoricalData(
          30,
          youtubeTotalViews,
          youtubeVideos.length,
        ),
        videos: youtubeVideos.map((v, index) => {
          const creator = DEMO_CREATORS.find((c) => c.id === v.creatorId);
          return {
            ...v,
            creatorName: creator?.name || "Unknown Creator",
            hasAnalyticsData: true,
            videoId: v._id,
            rank: index + 1,
            medianMultiplier: (v.views / medianViews).toFixed(1),
            creator: {
              name: creator?.name || "Unknown Creator",
              followers: creator?.followers || 0,
            },
          };
        }),
      },
      {
        platform: "tiktok",
        totalVideos: tiktokVideos.length,
        currentViews: tiktokTotalViews,
        currentLikes: tiktokVideos.reduce((sum, v) => sum + v.likes, 0),
        currentComments: tiktokVideos.reduce((sum, v) => sum + v.comments, 0),
        currentShares: tiktokVideos.reduce((sum, v) => sum + v.shares, 0),
        viewsGrowth24h: 28.4,
        likesGrowth24h: 32.1,
        commentsGrowth24h: 29.8,
        sharesGrowth24h: 35.6,
        historicalData: generateHistoricalData(
          30,
          tiktokTotalViews,
          tiktokVideos.length,
        ),
        videos: tiktokVideos.map((v, index) => {
          const creator = DEMO_CREATORS.find((c) => c.id === v.creatorId);
          return {
            ...v,
            creatorName: creator?.name || "Unknown Creator",
            hasAnalyticsData: true,
            videoId: v._id,
            rank: index + 1,
            medianMultiplier: (v.views / medianViews).toFixed(1),
            creator: {
              name: creator?.name || "Unknown Creator",
              followers: creator?.followers || 0,
            },
          };
        }),
      },
      {
        platform: "instagram",
        totalVideos: instagramVideos.length,
        currentViews: instagramTotalViews,
        currentLikes: instagramVideos.reduce((sum, v) => sum + v.likes, 0),
        currentComments: instagramVideos.reduce(
          (sum, v) => sum + v.comments,
          0,
        ),
        currentShares: instagramVideos.reduce((sum, v) => sum + v.shares, 0),
        viewsGrowth24h: 8.9,
        likesGrowth24h: 11.2,
        commentsGrowth24h: 7.4,
        sharesGrowth24h: 9.8,
        historicalData: generateHistoricalData(
          30,
          instagramTotalViews,
          instagramVideos.length,
        ),
        videos: instagramVideos.map((v, index) => {
          const creator = DEMO_CREATORS.find((c) => c.id === v.creatorId);
          return {
            ...v,
            creatorName: creator?.name || "Unknown Creator",
            hasAnalyticsData: true,
            videoId: v._id,
            rank: index + 1,
            medianMultiplier: (v.views / medianViews).toFixed(1),
            creator: {
              name: creator?.name || "Unknown Creator",
              followers: creator?.followers || 0,
            },
          };
        }),
      },
    ],
    campaignSummary: {
      totalContracts: DEMO_CREATORS.length,
      creatorsWithVideos: DEMO_CREATORS.length,
      totalVideos: DEMO_VIDEOS.length,
    },
    campaign: {
      teamVideoReviewRequired: false,
    },
    videoBreakdown: {
      all: {
        count: DEMO_VIDEOS.length,
        percentage: 100,
        totalViews: totalViews,
        medianViews: medianViews,
      },
      approved: {
        count: approvedVideos.length,
        percentage: Math.round(
          (approvedVideos.length / DEMO_VIDEOS.length) * 100,
        ),
        totalViews: approvedVideos.reduce((sum, v) => sum + v.views, 0),
        medianViews: Math.floor(
          approvedVideos.reduce((sum, v) => sum + v.views, 0) /
            approvedVideos.length,
        ),
      },
      rejected: {
        count: rejectedVideos.length,
        percentage: Math.round(
          (rejectedVideos.length / DEMO_VIDEOS.length) * 100,
        ),
        totalViews: rejectedVideos.reduce((sum, v) => sum + v.views, 0),
        medianViews:
          rejectedVideos.length > 0
            ? Math.floor(
                rejectedVideos.reduce((sum, v) => sum + v.views, 0) /
                  rejectedVideos.length,
              )
            : 0,
      },
      pending: {
        count: pendingVideos.length,
        percentage: Math.round(
          (pendingVideos.length / DEMO_VIDEOS.length) * 100,
        ),
        totalViews: pendingVideos.reduce((sum, v) => sum + v.views, 0),
        medianViews:
          pendingVideos.length > 0
            ? Math.floor(
                pendingVideos.reduce((sum, v) => sum + v.views, 0) /
                  pendingVideos.length,
              )
            : 0,
      },
    },
    creatorSignups: generateCreatorSignupData(DEMO_CREATORS.length),
  };
};

// Demo Leaderboard Data for Analytics (shows creator performance)
export const getDemoLeaderboard = (appName: string = "TechFlow Pro") => {
  const allVideos = generateDemoVideos(appName);
  // Calculate stats for each creator
  const creatorStats = DEMO_CREATORS.map((creator) => {
    const creatorVideos = allVideos.filter(
      (v) => v.creatorId === creator.id && v.status === "approved",
    );
    const totalViews = creatorVideos.reduce((sum, v) => sum + v.views, 0);
    const totalLikes = creatorVideos.reduce((sum, v) => sum + v.likes, 0);
    const totalVideos = creatorVideos.length;

    return {
      creatorId: creator.id,
      name: creator.name,
      avatarUrl: creator.avatarUrl,
      totalViews,
      totalLikes,
      totalVideos,
      avgViews: totalVideos > 0 ? Math.floor(totalViews / totalVideos) : 0,
    };
  })
    .filter((creator) => creator.totalVideos > 0) // Only include creators with videos
    .sort((a, b) => b.totalViews - a.totalViews); // Sort by total views

  return creatorStats;
};

// Demo Partnership/Contract Data
export const getDemoPartnership = () => {
  return {
    _id: DEMO_IDS.contractId,
    status: "active" as const,
    app: DEMO_APP,
    campaign: DEMO_CAMPAIGN,
    creatorId: DEMO_IDS.creatorId,
    appId: DEMO_IDS.appId,
    campaignId: DEMO_IDS.campaignId,
    signedAt: Date.now() - 20 * 24 * 60 * 60 * 1000, // 20 days ago
    terms: {
      flatFee: 2500,
      bonusPerVideo: 100,
      viewsThreshold: 10000,
      totalVideosRequired: 10,
    },
    _creationTime: Date.now() - 20 * 24 * 60 * 60 * 1000,
  };
};

// Demo Competitors Data
export const getDemoCompetitors = () => {
  return [
    {
      _id: "demo_competitor_1",
      name: "ProductivityPro",
      description: "AI-powered assistant with smart suggestions",
      logo: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=200&h=200&fit=crop&crop=center",
      website: "https://productivitypro.app",
      category: "Productivity Tools",
      pricing: "Free tier + $19/month",
      keyFeatures: ["Smart suggestions", "Task review", "Error detection"],
      marketPosition: "Direct competitor",
    },
    {
      _id: "demo_competitor_2",
      name: "WorkflowStudio",
      description: "Integrated workspace with workflow automation",
      logo: "https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=200&h=200&fit=crop&crop=center",
      website: "https://workflowstudio.app",
      category: "Workflow & Automation",
      pricing: "$29/month",
      keyFeatures: [
        "Workflow automation",
        "Team collaboration",
        "Project templates",
      ],
      marketPosition: "Indirect competitor",
    },
  ];
};

// Demo Video Projects Data for VideoFeedback
export const getDemoVideoProjects = (appName: string = "TechFlow Pro") => {
  return [
    {
      projectId: "demo_project_001" as Id<"creatorVideoProjects">,
      title: `${appName} Product Demo Video`,
      contractId: DEMO_IDS.contractId,
      contractVideoId: undefined,
      status: "brand_reviewing" as const,
      createdAt: Date.now() - 5 * 24 * 60 * 60 * 1000, // 5 days ago
      creatorId: "demo_creator_002",
      versions: [
        {
          id: "demo_version_001" as Id<"creatorVideoVersion">,
          status: "brand_reviewing" as const,
          videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
          duration: 180, // 3 minutes
        },
        {
          id: "demo_version_002" as Id<"creatorVideoVersion">,
          status: "approved" as const,
          videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
          duration: 195, // 3 minutes 15 seconds
        },
      ],
    },
    {
      projectId: "demo_project_002" as Id<"creatorVideoProjects">,
      title: `${appName} Tutorial Series - Episode 1`,
      contractId: DEMO_IDS.contractId,
      contractVideoId: undefined,
      status: "draft" as const,
      createdAt: Date.now() - 3 * 24 * 60 * 60 * 1000, // 3 days ago
      creatorId: "demo_creator_005",
      versions: [
        {
          id: "demo_version_003" as Id<"creatorVideoVersion">,
          status: "draft" as const,
          videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
          duration: 420, // 7 minutes
        },
      ],
    },
  ];
};

// Demo Competitor Videos Data
export const getDemoCompetitorVideos = (_appName: string = "TechFlow Pro") => {
  return [
    {
      id: "comp_video_1",
      url: "https://www.tiktok.com/@productivitytips/video/7234567890123456789",
      description:
        "Why ProductivityPro is better than other AI tools! #productivity #ai #workflow",
      platform: "tiktok" as const,
      views: 45000,
      likes: 3200,
      comments: 180,
      shares: 450,
      userHandle: "productivitytips",
      hashtags: ["#productivity", "#ai", "#workflow", "#tools"],
      campaignIds: ["TechFlow Pro Launch Campaign"],
    },
    {
      id: "comp_video_2",
      url: "https://www.tiktok.com/@techreview/video/7234567890123456790",
      description:
        "WorkflowStudio demo - this is game changing! #workflow #automation #productivity",
      platform: "tiktok" as const,
      views: 32000,
      likes: 2800,
      comments: 95,
      shares: 320,
      userHandle: "techreview",
      hashtags: ["#workflow", "#automation", "#productivity", "#efficiency"],
      campaignIds: ["TechFlow Pro Launch Campaign"],
    },
  ];
};

// Demo Payouts Data
export const getDemoPayouts = (appName: string = "TechFlow Pro") => {
  const allVideos = generateDemoVideos(appName);
  const approvedVideos = allVideos.filter((v) => v.status === "approved");
  const totalEarnings = approvedVideos.reduce((sum, v) => sum + v.earnings, 0);

  const creatorPayouts = DEMO_CREATORS.map((creator) => {
    const creatorApprovedVideos = approvedVideos.filter(
      (v) => v.creatorId === creator.id,
    );
    const pendingAmount = creatorApprovedVideos.reduce(
      (sum, v) => sum + v.earnings,
      0,
    );

    return {
      _id: creator.id as Id<"creators">,
      name: creator.name,
      avatarUrl: creator.avatarUrl,
      pendingAmount,
      paidAmount: 0,
      contract: {
        _id: creator.contractId as Id<"creatorContracts">,
        status: "active" as const,
      },
      videos: creatorApprovedVideos,
    };
  }).filter((creator) => creator.pendingAmount > 0); // Only include creators with earnings

  return {
    totalPending: totalEarnings,
    totalPaid: 0,
    creators: creatorPayouts,
  };
};

// Note: Chat messages removed - demo mode is only for app founders, not creators
export const getDemoChatMessages = () => {
  return []; // Empty array - no creator chat in founder demo mode
};
